import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { SpSharedModule } from '../../shared/sp-shared.module';
import { SkyPlusAnalyticsService, MyCompletedTask } from '../../dashboard/skyplus-analytics.service';

/**
 * "Completed" drill-down — the tasks the logged-in user has completed, behind the dashboard
 * Completed card. Read-only: a row opens the (archived) instance view. Data comes from
 * {@code GET /api/wf/analytics/my-completed}, which reads the audit trail
 * ({@code action = TASK_COMPLETED, actor = me}).
 */
@Component({
  standalone: true,
  selector: 'wf-my-completed',
  imports: [CommonModule, FormsModule, SpSharedModule],
  template: `
    <div class="mc-page">
      <sp-header></sp-header>

      <main class="mc-main">
        <div class="mc-head">
          <div class="mc-title-wrap">
            <h1 class="mc-h1">Tasks I have completed</h1>
            <span class="mc-count" *ngIf="!loading">{{ filtered().length }} completed</span>
          </div>
          <button class="mc-refresh" (click)="load()" [disabled]="loading" title="Refresh">
            <span class="material-icons">refresh</span>
          </button>
        </div>

        <div class="mc-controls">
          <div class="mc-search">
            <span class="material-icons">search</span>
            <input [(ngModel)]="search" placeholder="Search by reference, subject or workflow">
          </div>
        </div>

        <div class="mc-loading" *ngIf="loading">
          <span class="material-icons spin">progress_activity</span> Loading your completed tasks…
        </div>

        <div class="mc-card" *ngIf="!loading">
          <div class="mc-empty" *ngIf="!filtered().length">
            <span class="material-icons">task_alt</span>
            <p>You have not completed any tasks yet.</p>
          </div>

          <div class="mc-scroll" *ngIf="filtered().length">
            <table class="mc-table">
              <thead>
                <tr>
                  <th class="c-wf">Workflow</th>
                  <th class="c-ref">Reference</th>
                  <th class="c-sub">Subject</th>
                  <th class="c-step">Step</th>
                  <th class="c-out">Outcome</th>
                  <th class="c-when">Completed</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let t of filtered()" (click)="open(t)"
                    [class.clickable]="!!t.instanceId" title="Open the instance">
                  <td class="c-wf">{{ t.wfKey || '—' }}</td>
                  <td class="c-ref"><span class="mc-ref">{{ t.businessKey || '—' }}</span></td>
                  <td class="c-sub">{{ t.title || '—' }}</td>
                  <td class="c-step">{{ t.taskName || '—' }}</td>
                  <td class="c-out"><span class="mc-badge" *ngIf="t.outcome">{{ t.outcome }}</span></td>
                  <td class="c-when">{{ t.completedAt ? (t.completedAt | date:'medium') : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  `,
  styles: [`
    .mc-page { min-height: 100%; background: #f5f6f8; }
    .mc-main { max-width: 1200px; margin: 0 auto; padding: 20px 24px 48px; }
    .mc-head { display: flex; align-items: center; gap: 12px; margin: 8px 0 16px; }
    .mc-title-wrap { flex: 1; display: flex; align-items: baseline; gap: 12px; }
    .mc-h1 { font-size: 22px; font-weight: 700; color: #1f2937; margin: 0; }
    .mc-count { font-size: 13px; color: #6b7280; }
    .mc-refresh { border: 1px solid rgba(0,0,0,0.1); background: #fff; border-radius: 8px;
      width: 38px; height: 38px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
    .mc-refresh:disabled { opacity: .5; cursor: default; }
    .mc-controls { margin-bottom: 12px; }
    .mc-search { display: inline-flex; align-items: center; gap: 6px; background: #fff;
      border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; padding: 6px 10px; width: 360px; max-width: 100%; }
    .mc-search .material-icons { color: #9ca3af; font-size: 18px; }
    .mc-search input { border: 0; outline: 0; flex: 1; font-size: 13px; background: transparent; }
    .mc-loading { display: flex; align-items: center; gap: 8px; color: #6b7280; padding: 24px 4px; }
    .spin { animation: mc-spin 1s linear infinite; }
    @keyframes mc-spin { to { transform: rotate(360deg); } }
    .mc-card { background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 12px; overflow: hidden; }
    .mc-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #9ca3af; padding: 48px 0; }
    .mc-empty .material-icons { font-size: 40px; }
    .mc-scroll { overflow-x: auto; }
    .mc-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .mc-table th { text-align: left; padding: 11px 14px; color: #6b7280; font-weight: 600;
      border-bottom: 1px solid rgba(0,0,0,0.08); white-space: nowrap; background: #fafafa; }
    .mc-table td { padding: 11px 14px; border-bottom: 1px solid rgba(0,0,0,0.05); color: #374151; vertical-align: middle; }
    .mc-table tr.clickable { cursor: pointer; }
    .mc-table tbody tr.clickable:hover { background: rgba(36,64,98,0.04); }
    .mc-ref { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px; color: #244062; }
    .mc-badge { display: inline-block; padding: 2px 8px; border-radius: 999px; background: #e8f0eb;
      color: #16704F; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
    .c-when { white-space: nowrap; color: #6b7280; }
  `]
})
export class WfMyCompletedComponent implements OnInit {
  private readonly analytics = inject(SkyPlusAnalyticsService);
  private readonly router = inject(Router);

  rows: MyCompletedTask[] = [];
  loading = true;
  search = '';

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.analytics.getMyCompleted(200).subscribe({
      next: (rows) => { this.rows = rows || []; this.loading = false; },
      error: () => { this.rows = []; this.loading = false; }
    });
  }

  filtered(): MyCompletedTask[] {
    const q = this.search.trim().toLowerCase();
    if (!q) { return this.rows; }
    return this.rows.filter(t =>
      [t.wfKey, t.businessKey, t.title, t.taskName, t.outcome]
        .some(v => (v || '').toLowerCase().includes(q)));
  }

  open(t: MyCompletedTask): void {
    if (!t.instanceId) { return; }
    this.router.navigate(['/workflow/r/instance', t.instanceId]);
  }
}
